import loadable from "@loadable/component";
import classnames from "classnames";
import { isLeft } from "fp-ts/lib/Either";
import i18n from "i18next";
import { inject } from "mobx-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Store } from "@inferno/renderer-shared-core";
import { filterEventDates } from "@inferno/renderer-shared-core";
import { CalendarPayloadFields, CalendarPayloadResolver } from "@ihr-radioedit/inferno-core";
import * as Webapi from "@ihr-radioedit/inferno-webapi";
import { getPaths } from "@ihr-radioedit/inferno-core";
import { ILog } from "@ihr-radioedit/inferno-core";
import { Heading } from "../../ui";
import "./FrontMatter.style.scss";
import { DisplayTime } from "../../ui";
import { Venue } from "../../ui";
import { ShareButtons } from "../../components/integrations/ShareButtons.component";

const EyebrowLoader = loadable(() => import("./EyebrowLoader.component"));
const Eyebrow = loadable(() => import("../../components/Eyebrow.component"));

const log = ILog.logger("Calendar FrontMatter");

interface FrontMatterProps {
  content: Webapi.PublishRecordFragment;
  block: Webapi.Block_SitesItemBlock_Fragment;
  eyebrow?: Webapi.Eyebrow;
  store?: Store;
  url?: string;
}

const EventDates = ({ event_date, hide_event_start_time, store }: CalendarPayloadFields & { store: Store }) => {
  const { t } = useTranslation();
  const [clicked, storeClicked] = useState(false);

  const toggleClicked = useCallback(() => {
    storeClicked(!clicked);
  }, [clicked, storeClicked]);
  const eventDates = filterEventDates(event_date?.value ?? []);
  const startDateValue = eventDates?.length > 0 ? eventDates[0] : event_date?.value[0];
  const startDate = startDateValue?.begin ? (
    <DisplayTime store={store} startDate={startDateValue.begin} hideEventStartTime={hide_event_start_time?.value} />
  ) : null;
  const additionalDatesValue = eventDates.slice(1, eventDates.length);

  return (
    <div className="dates-container">
      <div>
        {startDate}
        {additionalDatesValue?.length ? (
          <span className="additional-dates-text" onClick={toggleClicked}>
            +{additionalDatesValue.length} {t("calendar_occurrences")}
          </span>
        ) : null}
      </div>
      {clicked ? (
        <div className="additional-dates">
          {additionalDatesValue.map((date, i) => {
            return (
              date?.begin && (
                <DisplayTime
                  key={i}
                  store={store}
                  startDate={date.begin}
                  hideEventStartTime={hide_event_start_time?.value}
                />
              )
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

export const CalendarFrontMatter = inject("store")(({ content, block, store, url }: FrontMatterProps) => {
  if (!store || !content || !block) {
    return null;
  }
  const resolvedPayload = CalendarPayloadResolver.decode(content.payload);
  if (isLeft(resolvedPayload)) {
    const errorMessage = `Payload did not validate! ${getPaths(resolvedPayload)}`;
    log.error(errorMessage);
    return null;
  }
  const payload = resolvedPayload.right;
  const { fields, ingestion } = payload;

  if (!fields) {
    return null;
  }

  const eyebrow = useMemo(() => {
    if (fields.is_sponsored) {
      return { topic_name: i18n.t("sponsored_content") };
    } else if (ingestion?.feed_vendor) {
      return { topic_name: i18n.t("partner_content") };
    }
    return null;
  }, [ingestion?.feed_vendor, fields.is_sponsored]);

  useEffect(() => {
    if (eyebrow) {
      store.storeBlock({
        ...block,
        type: "item",
        eyebrow,
      });
    }
  }, [block, eyebrow, store]);

  const headerCss = classnames("content-header", {
    "front-matter": block.tags?.includes("display-hints/front-matter"),
  });

  return (
    <header className={headerCss}>
      {eyebrow ? <Eyebrow {...eyebrow} /> : <EyebrowLoader content={content} block={block} />}
      <Heading level={1}>{content.summary.title}</Heading>
      <hr />
      <Venue venueName={fields.venue_name?.value} venueAddress={fields.venue_address?.value} />
      <div className="date-share-icons-block">
        <EventDates store={store} {...fields} />
        <ShareButtons url={url} title={content.summary.title} desc={content.summary.description} setStyles={false} />
      </div>
    </header>
  );
});

export default CalendarFrontMatter;
